import {
  FREQUENCY_CACHE_KEY_PREFIX,
  FREQUENCY_CACHE_MAX_ENTRIES,
  FREQUENCY_CACHE_TTL_MS,
} from './constants';
import type { DisplayFrequency, FrequencyCacheEntry, FrequencyStatus } from './types';

/** Prioridad de status para no degradar shown→dismissed→… */
const STATUS_PRIORITY: Record<FrequencyStatus, number> = {
  shown: 1,
  dismissed: 2,
  completed: 2,
};

/**
 * Namespace short y estable derivado del apiKey. NO es cripto: solo evita
 * que dos apps que comparten el navegador pisen sus caches. El apiKey
 * público (`pk_*`) ya es workspace-único, así que usar sus últimos 16
 * caracteres alcanza para diferenciar.
 */
export function namespaceFromApiKey(apiKey: string): string {
  return apiKey.slice(-16) || 'default';
}

/**
 * Cache persistente de "qué guías ya vio/cerró este usuario". Vive en
 * `localStorage` con key namespaceada para evitar colisión entre
 * workspaces que corren en el mismo navegador.
 *
 * No es source of truth — el backend siempre manda. Esta cache solo
 * evita el flash visual de re-pintar una guía que el usuario ya cerró
 * antes de que el roundtrip al backend termine.
 */
export class GuideFrequencyCache {
  private readonly storageKey: string;
  private cache: Map<string, FrequencyCacheEntry>;

  constructor(namespace: string) {
    this.storageKey = `${FREQUENCY_CACHE_KEY_PREFIX}${namespace}`;
    this.cache = this.load();
    this.prune();
  }

  /**
   * Decide si una guía debe filtrarse localmente antes de pintarla.
   *
   *   - `every_visit` / `always` → nunca filtra (siempre re-pinta).
   *   - `once` → cualquier entry registrada filtra (verla ya cuenta).
   *   - `until_dismissed` → si fue descartada O COMPLETADA (responder un
   *     form / terminar un walkthrough es más fuerte que descartar — sin
   *     esto, una encuesta seguiría preguntando después de respondida).
   */
  shouldFilter(guideId: string, displayFrequency: DisplayFrequency): boolean {
    if (displayFrequency === 'every_visit' || displayFrequency === 'always') return false;

    const entry = this.cache.get(guideId);
    if (!entry) return false;

    if (displayFrequency === 'once') return true;
    if (displayFrequency === 'until_dismissed') {
      return entry.status === 'dismissed' || entry.status === 'completed';
    }
    // `until_answered` (forms): solo COMPLETAR filtra localmente. El descarte
    // no la silencia — y si las props ya tienen valor por otro medio, es el
    // BACKEND quien la excluye del resolve (aquí no conocemos los traits).
    if (displayFrequency === 'until_answered') return entry.status === 'completed';
    return false;
  }

  /**
   * Registra/actualiza la entry de una guía. Nunca degrada un estado más
   * fuerte (no convierte `dismissed` en `shown` por un re-render). Persiste
   * cada llamada — escribir a localStorage es lo bastante barato.
   */
  record(guideId: string, status: FrequencyStatus): void {
    const existing = this.cache.get(guideId);
    if (existing && STATUS_PRIORITY[status] < STATUS_PRIORITY[existing.status]) {
      return;
    }
    this.cache.set(guideId, {
      guideId,
      status,
      lastInteractionAt: Date.now(),
    });
    this.persist();
  }

  /** Limpia el cache (botón "reset" del demo, tests). */
  clear(): void {
    this.cache.clear();
    this.persist();
  }

  /** Útil para debugging — devuelve un snapshot. */
  snapshot(): FrequencyCacheEntry[] {
    return Array.from(this.cache.values());
  }

  /**
   * Limpia entries expiradas por TTL y, si aún excede el límite, descarta
   * las más viejas hasta caber. Se ejecuta una vez en el constructor; no
   * vale la pena correrlo a cada lectura.
   */
  private prune(): void {
    const now = Date.now();
    let mutated = false;

    for (const [id, entry] of this.cache) {
      if (now - entry.lastInteractionAt > FREQUENCY_CACHE_TTL_MS) {
        this.cache.delete(id);
        mutated = true;
      }
    }

    if (this.cache.size > FREQUENCY_CACHE_MAX_ENTRIES) {
      const sorted = Array.from(this.cache.entries()).sort(
        (a, b) => a[1].lastInteractionAt - b[1].lastInteractionAt,
      );
      const excess = this.cache.size - FREQUENCY_CACHE_MAX_ENTRIES;
      for (let i = 0; i < excess; i++) {
        const tuple = sorted[i];
        if (tuple) this.cache.delete(tuple[0]);
      }
      mutated = true;
    }

    if (mutated) this.persist();
  }

  private load(): Map<string, FrequencyCacheEntry> {
    if (typeof localStorage === 'undefined') return new Map();
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return new Map();
      const entries: Array<[string, FrequencyCacheEntry]> = [];
      for (const item of parsed) {
        if (
          item &&
          typeof item === 'object' &&
          typeof (item as FrequencyCacheEntry).guideId === 'string' &&
          typeof (item as FrequencyCacheEntry).lastInteractionAt === 'number' &&
          ['shown', 'dismissed', 'completed'].includes((item as FrequencyCacheEntry).status)
        ) {
          const e = item as FrequencyCacheEntry;
          entries.push([e.guideId, e]);
        }
      }
      return new Map(entries);
    } catch {
      return new Map();
    }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const serialized = JSON.stringify(Array.from(this.cache.values()));
      localStorage.setItem(this.storageKey, serialized);
    } catch {
      // QuotaExceeded o similar — el cache es opcional, no rompemos la app.
    }
  }
}
